import type React from 'react';
import { t } from '@lingui/core/macro';

import FenceIcon from '@rover/icons/svg/fence.svg';
import NoCatIcon from '@rover/icons/svg/no-cats.svg';
import NoDogIcon from '@rover/icons/svg/no-dogs.svg';
import NotFixedIcon from '@rover/icons/svg/not-fixed.svg';
import PetsIcon from '@rover/icons/svg/pets.svg';
import PuppyIcon from '@rover/icons/svg/puppy.svg';
import type { SearchFilters } from '@rover/types/src/SearchFilters';

import { ValuePropFilterOptionType } from '../../../utilities/analyticsEvents';

import { ValuePropFilter } from './filters';
import type SquareIconButton from './SquareIconButton';

type SquareIconButtonProps = React.ComponentProps<typeof SquareIconButton>;

export type ValuePropTileConfig = {
  icon: SquareIconButtonProps['icon'];
  title: string;
  searchFilterOverrides: Partial<SearchFilters>;
  analyticsOption: ValuePropFilterOptionType;
};

export const getTileConfig = (filter: ValuePropFilter): ValuePropTileConfig => {
  switch (filter) {
    case ValuePropFilter.NoCat:
      return {
        icon: NoCatIcon,
        title: t`Doesn't own a cat`,
        searchFilterOverrides: { noCats: true },
        analyticsOption: ValuePropFilterOptionType.NO_CAT,
      };
    case ValuePropFilter.NoDog:
      return {
        icon: NoDogIcon,
        title: t`Doesn't own a dog`,
        searchFilterOverrides: { personDoesNotHaveDogs: true },
        analyticsOption: ValuePropFilterOptionType.NO_DOG,
      };
    case ValuePropFilter.FencedYard:
      return {
        icon: FenceIcon,
        title: t`Has a fenced yard`,
        searchFilterOverrides: { hasFencedYard: true },
        analyticsOption: ValuePropFilterOptionType.HAS_FENCED_YARD,
      };
    case ValuePropFilter.NotFixed:
      return {
        icon: NotFixedIcon,
        title: t`Accepts non-spayed or neutered pets`,
        searchFilterOverrides: { unspayedFemales: true, nonNeuteredMales: true },
        analyticsOption: ValuePropFilterOptionType.NOT_FIXED,
      };
    case ValuePropFilter.OnlyOnePet:
      return {
        icon: PetsIcon,
        title: t`Accepts only one pet at a time`,
        searchFilterOverrides: { acceptsOnlyOneClient: true },
        analyticsOption: ValuePropFilterOptionType.ONLY_ONE_PET,
      };
    case ValuePropFilter.Puppies:
      return {
        icon: PuppyIcon,
        title: t`Accepts puppies`,
        searchFilterOverrides: { puppy: true },
        analyticsOption: ValuePropFilterOptionType.ACCEPTS_PUPPIES,
      };
    default: {
      const exhaustiveCheck: never = filter;
      return exhaustiveCheck;
    }
  }
};
